import { useState, useEffect, useContext } from 'react';
import api from './api/axiosInstance';
import { UserContext } from './UserContext';
import { Link } from 'react-router-dom';
import Container from 'react-bootstrap/Container';
import Sidebar from './Sidebar.js';
import { Form, Row, Col, Card, Table, Button, Image} from 'react-bootstrap';
import CountryFilter from './CountryFilter';
import Pagination from 'react-bootstrap/Pagination';
import Breadcrumb from './Breadcrumb';
import { isAdminType} from './utils/roleUtils';
import { FaCaretUp, FaCaretDown } from 'react-icons/fa';
import icNoCountries from './assets/img/ic-empty-box.svg';

const CountryList = () => {
    const [countries, setCountries] = useState([]);
    const [filteredCountries, setFilteredCountries] = useState([]);
    
    const [countriesPerPage, setCountriesPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    const indexOfLastCountry = currentPage * countriesPerPage;
    const indexOfFirstCountry = indexOfLastCountry - countriesPerPage;
    const currentCountries = filteredCountries.slice(indexOfFirstCountry, indexOfLastCountry);
    const [searchQuery, setSearchQuery] = useState('');
    const { user } = useContext(UserContext);
    
    const [sortOrderId, setSortOrderId] = useState('asc');
    const [sortOrderName, setSortOrderName] = useState('asc'); // Sorting order for company name
    const [sortOrderStatus, setSortOrderStatus] = useState('asc'); // Sorting order for status
    const [sortOrderCities, setSortOrderCities] = useState(null);
    
    const [selectedCountry] = useState('');
    const [tickets, setTickets] = useState([]);


    // Safely compute number of cities (adapt to your data shape)
    const getCitiesCount = (country) => {
        return typeof country?.citiesNumber === 'number'
            ? country.citiesNumber
            : 0;
    };

    // Sorting utilities
    const toggleOrder = (curr) => (curr === 'asc' ? 'desc' : 'asc');

    const sortCountriesById = () => {
        const next = toggleOrder(sortOrderId) || 'asc';
        setSortOrderId(next);
        setSortOrderName(null); setSortOrderCities(null); setSortOrderStatus(null);
        setFilteredCountries(prev => {
            const arr = [...prev];
            arr.sort((a, b) => {
                const aId = String(a?.country?.id ?? '').toLowerCase();
                const bId = String(b?.country?.id ?? '').toLowerCase();
                return next === 'asc' ? aId.localeCompare(bId) : bId.localeCompare(aId);
            });
            return arr;
        });
    };

    const sortCountriesByName = () => {
        const next = toggleOrder(sortOrderName) || 'asc';
        setSortOrderName(next);
        setSortOrderId(null); setSortOrderCities(null); setSortOrderStatus(null);
        setFilteredCountries(prev => {
            const arr = [...prev];
            arr.sort((a, b) => {
            const aName = String(a?.country?.name ?? '').toLowerCase();
            const bName = String(b?.country?.name ?? '').toLowerCase();
            return next === 'asc' ? aName.localeCompare(bName) : bName.localeCompare(aName);
            });
            return arr;
        });
    };

    const sortCountriesByCities = () => {
        const next = toggleOrder(sortOrderCities) || 'asc';
        setSortOrderCities(next);
        setSortOrderId(null); setSortOrderName(null); setSortOrderStatus(null);
        setFilteredCountries(prev => {
            const arr = [...prev];
            arr.sort((a, b) => {
                const aC = getCitiesCount(a);
                const bC = getCitiesCount(b);
                return next === 'asc' ? aC - bC : bC - aC;
            });
            return arr;
        });
    };

    const sortCountriesByStatus = () => {
        const next = toggleOrder(sortOrderStatus) || 'asc';
        setSortOrderStatus(next);
        setSortOrderId(null); setSortOrderName(null); setSortOrderCities(null);
        setFilteredCountries(prev => {
            const arr = [...prev];
            arr.sort((a, b) => {
            const aS = a?.activityStatus ? 1 : 0;
            const bS = b?.activityStatus ? 1 : 0;
            return next === 'asc' ? aS - bS : bS - aS;
            });
            return arr;
        });
    };

    useEffect(() => {
        const fetchTickets = async () => {
            try {
                const res = await api.get(`/api/tickets`);
                setTickets(res.data);
            } catch (error) {
                console.error('Error fetching tickets:', error);
            }   
        };
        fetchTickets();
    }, []);

    useEffect(() => {
        if (user && isAdminType(user.role)) {
            fetchCountries();
        }
    }, [user]);

    const fetchCountries = async () => {
        try {
            const res = await api.get(`/api/countries`);
            setCountries(res.data);
            setFilteredCountries(res.data); // Initialize with full list
        } catch (error) {
            console.error('Error fetching companies:', error);
        }
    };

    // Handle search filter
    useEffect(() => {
        const lowercasedQuery = searchQuery.toLowerCase();
        const filteredResults = countries.filter((country) => 
            country.country.name.toLowerCase().includes(lowercasedQuery) &&
            (selectedCountry === '' || country.country.id === selectedCountry)
        );
        
        setFilteredCountries(filteredResults);
        setCurrentPage(1); // ✅ Reset to first page when filtering
    }, [searchQuery, selectedCountry, countries]);
    
    // Toggle Activity status
    const toggleActivityStatus = async (countryId, currentStatus) => {
        try {
            // Send a patch request to toggle the status
            const response = await api.patch(`/api/countries/toggle-status/${countryId}`, {} );
            
            const updatedStatus = response.data.activityStatus;
            console.log(`Country ID: ${countryId}, Updated Status: ${updatedStatus}`);
    
            // Update state to reflect the change
            setCountries((prevCountries) =>
                prevCountries.map((country) =>
                    country._id === countryId
                        ? { ...country, activityStatus: updatedStatus }
                        : country
                )
            );
        } catch (error) {
            console.error('Error toggling activity status:', error);
        }
    };
    
    if (!user || !isAdminType(user.role)) {
        return <div>You are not authorized to view this page.</div>;
    }

    const totalPages = Math.ceil(filteredCountries.length / countriesPerPage);

    const handlePageChange = (pageNumber) => setCurrentPage(pageNumber);

    const noCountries = filteredCountries.length === 0;

    return (
        <>
            <Sidebar ticketCount={tickets.length} />
            <main className="main-content flex-grow-1">
                <Container>
                    <Row>
                        <Col>
                            <Breadcrumb />
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <Card>
                                <Card.Body>
                                    <Row>
                                        <Col xs={6}>
                                            <h1>Countries</h1>
                                        </Col>
                                    </Row>
                                    <Row>
                                        <Col xs={12}>
                                            <CountryFilter
                                                searchQuery={searchQuery}
                                                setSearchQuery={setSearchQuery}
                                                countries={countries}
                                            />
                                        </Col>
                                    </Row>
                                    {noCountries ? (
                                        <Row>
                                            <Col md={12}>
                                                <Row className="mb-3">
                                                    <Col md={6} className="no-result offset-md-3 d-flex flex-column justify-content-center align-items-center text-center">
                                                        <Image src={icNoCountries} alt="No Countries" className="mb-4" />
                                                        <h2 className="mb-3">No Countries yet.</h2>
                                                        <p className="mb-4">Your Countries will be displayed here.</p>
                                                        <Button as={Link} to="/country-add" variant="primary">Add Country</Button>
                                                    </Col>
                                                </Row>
                                            </Col>
                                        </Row>
                                    ) : (
                                        <Row>
                                            <Col>
                                                <div className="table-responsive list">
                                                    <Table>
                                                        <thead>
                                                            <tr>
                                                                <th className="th-fixed-city-id" onClick={() => sortCountriesById()} style={{ cursor: 'pointer' }}>
                                                                    <div>
                                                                        ID
                                                                        <span>
                                                                            <FaCaretUp   className={sortOrderId === 'asc'  ? 'text-primary' : ''} />
                                                                            <FaCaretDown className={sortOrderId === 'desc' ? 'text-primary' : ''} />
                                                                        </span>
                                                                    </div>
                                                                </th>

                                                                <th className="th-fixed-city-name" onClick={() => sortCountriesByName()} style={{ cursor: 'pointer' }}>
                                                                    <div>
                                                                        Name
                                                                        <span>
                                                                            <FaCaretUp   className={sortOrderName === 'asc'  ? 'text-primary' : ''} />
                                                                            <FaCaretDown className={sortOrderName === 'desc' ? 'text-primary' : ''} />
                                                                        </span>
                                                                    </div>
                                                                </th>

                                                                <th className="th-fixed-city-comments" onClick={() => sortCountriesByCities()} style={{ cursor: 'pointer' }}>
                                                                    <div>
                                                                        Cities
                                                                        <span>
                                                                            <FaCaretUp   className={sortOrderCities === 'asc'  ? 'text-primary' : ''} />
                                                                            <FaCaretDown className={sortOrderCities === 'desc' ? 'text-primary' : ''} />
                                                                        </span>
                                                                    </div>
                                                                </th>

                                                                <th className="th-fixed-city-status" onClick={() => sortCountriesByStatus()} style={{ cursor: 'pointer' }}>
                                                                    <div>
                                                                        Active
                                                                        <span>
                                                                            <FaCaretUp   className={sortOrderStatus === 'asc'  ? 'text-primary' : ''} />
                                                                            <FaCaretDown className={sortOrderStatus === 'desc' ? 'text-primary' : ''} />
                                                                        </span>
                                                                    </div>
                                                                </th>

                                                                <th></th>
                                                            </tr>
                                                        </thead>

                                                        <tbody>
                                                            {currentCountries.map((country) => {
                                                                const idVal   = country?.country?.id ?? '';
                                                                const nameVal = country?.country?.name ?? '';
                                                                const citiesCount = getCitiesCount(country);

                                                                return (
                                                                    <tr key={country._id} style={{ cursor: 'pointer' }}>
                                                                        <td>{idVal}</td>
                                                                        <td>{nameVal}</td>
                                                                        <td>{citiesCount}</td>
                                                                        <td>
                                                                            <div className="form-check form-switch m-0">
                                                                                <input
                                                                                className="form-check-input"
                                                                                type="checkbox"
                                                                                role="switch"
                                                                                id={`status-switch-${country._id}`}
                                                                                checked={!!country.activityStatus}
                                                                                onChange={() => toggleActivityStatus(country._id, country.activityStatus)}
                                                                                />
                                                                            </div>
                                                                        </td>
                                                                        <td className="text-end">
                                                                            <Link to={`/country-edit-general/${country._id}`} className="btn btn-sm btn-outline-secondary">
                                                                                Edit
                                                                            </Link>
                                                                        </td>
                                                                    </tr>
                                                                );
                                                            })}
                                                        </tbody>
                                                    </Table>
                                                </div>
                                            </Col>
                                        </Row>
                                    )}
                                
                                    {/* Pagination Row with Total Count, Items Per Page Selector & "Go to Page" Input */}
                                    <Row className="pagination-wrapper">
                                        <Col className="d-flex align-items-center">
                                            <span>Total&nbsp;{filteredCountries.length} Countries</span>
                                            <Pagination>
                                                <Pagination.Prev 
                                                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} 
                                                    disabled={currentPage === 1}
                                                />
                                                {[...Array(totalPages).keys()].map(number => (
                                                    <Pagination.Item 
                                                        key={number + 1} 
                                                        active={number + 1 === currentPage} 
                                                        onClick={() => handlePageChange(number + 1)}
                                                    >
                                                        {number + 1}
                                                    </Pagination.Item>
                                                ))}
                                                <Pagination.Next 
                                                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))} 
                                                    disabled={currentPage === totalPages}
                                                />
                                            </Pagination>
                                        </Col>

                                        <Col className="pagination-controls">
                                            <div className="pagination-controls-per-page">
                                                <Form.Select
                                                    value={countriesPerPage}
                                                    onChange={(e) => setCountriesPerPage(Number(e.target.value))} // ✅ Updates state
                                                    style={{ width: 'auto', marginRight: '10px' }}
                                                >
                                                    <option value="10">10 / page</option>
                                                    <option value="20">20 / page</option>
                                                    <option value="40">40 / page</option>
                                                </Form.Select>
                                            </div>
                                            <label className="pagination-controls-goto-label" htmlFor="goto-page">
                                                Go to
                                            </label>
                                            <Form.Control
                                                type="number"
                                                value={currentPage}
                                                className="pagination-controls-goto-input"
                                                onChange={(e) => {
                                                    const newPage = Number(e.target.value);
                                                    if (newPage >= 1 && newPage <= totalPages) {
                                                        setCurrentPage(newPage); // ✅ Only valid pages
                                                    }
                                                }}
                                                onBlur={() => {
                                                    if (currentPage < 1) setCurrentPage(1);
                                                    if (currentPage > totalPages) setCurrentPage(totalPages);
                                                }}
                                                style={{ textAlign: 'center' }}
                                            />
                                        </Col>
                                    </Row>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                    <Row className="buttons-row">
                        <Col>
                            <Link to="/country-add" className="btn btn-primary">
                                Add New Country
                            </Link>
                        </Col>
                    </Row>
                </Container>
            </main>
        </>
        
    );
};

export default CountryList;
